import { trackEvent } from "./api/tracker";
import { LibCategories, waitForConsent } from "./consent";
import { NoomError } from "./error/NoomError";
import { trackEventIfNotUnloading } from "./lifecycle";

const loaderCache: Record<string, Promise<void>> = {};

class ScriptLoadError extends NoomError {
  constructor(public readonly src: string) {
    super(`Failed to load script: ${src}`);
  }

  override trackMetric() {
    trackEventIfNotUnloading("OnScriptLoadError", {
      src: this.src,
    });
    return true;
  }
}

type HtmlAttrTypes = Partial<HTMLScriptElement>;

export const pendingScripts = new Set<string>();

export async function loadScript(
  src: string,
  categories: [LibCategories, ...LibCategories[]],
  {
    enableCORS = true,
    htmlAttr: { dataset, ...htmlAttributes } = {},
  }: {
    enableCORS?: boolean;
    htmlAttr?: HtmlAttrTypes;
  } = {}
): Promise<void> {
  pendingScripts.add(src);
  await waitForConsent(categories);

  if (!loaderCache[src]) {
    loaderCache[src] = new Promise((resolve, reject) => {
      const longRunningLoadTimer = setTimeout(() => {
        trackEvent(`OnLongRunningScriptLoad`, {
          src,
        });
      }, 5000);

      const script = document.createElement("script");
      if (htmlAttributes) {
        Object.assign(script, htmlAttributes);
      }
      if (dataset) {
        Object.assign(script.dataset, dataset);
      }

      // Before setting src
      script.onload = () => {
        clearTimeout(longRunningLoadTimer);
        pendingScripts.delete(src);

        resolve();
      };

      script.onerror = () => {
        clearTimeout(longRunningLoadTimer);
        pendingScripts.delete(src);

        // Purge cache to allow retry
        delete loaderCache[src];

        reject(new ScriptLoadError(src));
      };

      script.async = true;
      if (enableCORS) {
        script.crossOrigin = "anonymous";
      }

      // Do these last
      script.src = src;
      document.body.appendChild(script);
    });
  }
  return loaderCache[src];
}
